import {
  Component,
  ChangeDetectionStrategy,
  input,
} from '@angular/core';

/**
 * Per-venue connection-state strip (GAP-FU-03).
 *
 * Always-visible operator strip showing one row per market venue:
 *
 *   ●  BINANCE       online        12 ms  ▲ 2.4k/s
 *   ●  COINBASE      degraded     188 ms  ▼ 8/s   (last seq 14:23:09Z)
 *   ●  OKX           offline        —     —      (reconnecting…)
 *
 * Inputs are venue rows — consumers (e.g. settings page, dashboard
 * status panel) build the array from whatever connection-state source
 * is local to them. Decoupling the visual primitive from the data
 * source keeps it composable across surfaces.
 *
 * Pair with the existing `app-global-websocket-status` (which shows
 * AGGREGATE health) — that component remains the navbar pill; this
 * component is the per-venue breakdown.
 */
export type VenueState = 'online' | 'degraded' | 'offline' | 'connecting' | 'unknown';

export interface VenueRow {
  readonly name: string;
  readonly state: VenueState;
  /** Round-trip latency in ms (or null when offline). */
  readonly latencyMs?: number | null;
  /** Messages-per-second throughput on this venue's feed. */
  readonly throughput?: number | null;
  /** Last successful sequence/heartbeat timestamp — surfaced when degraded/offline. */
  readonly lastSeenAt?: string | null;
  /** Optional reason string shown for non-online states. */
  readonly reason?: string | null;
}

@Component({
  selector: 'app-venue-connection-strip',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './venue-connection-strip.component.html',
  styleUrls: ['./venue-connection-strip.component.scss'],
})
export class VenueConnectionStripComponent {
  /** Venue rows. Order is preserved verbatim. */
  readonly venues = input.required<readonly VenueRow[]>();
  /** Visual density. */
  readonly density = input<'compact' | 'normal'>('normal');

  protected stateClass(state: VenueState): string {
    return `is-${state}`;
  }

  protected stateLabel(state: VenueState): string {
    switch (state) {
      case 'online':     return 'Online';
      case 'degraded':   return 'Degraded';
      case 'offline':    return 'Offline';
      case 'connecting': return 'Connecting';
      case 'unknown': return 'Unknown';
    }
  }

  protected formatLatency(ms: number | null | undefined): string {
    if (ms == null) return '—';
    return `${ms} ms`;
  }

  protected formatThroughput(perSec: number | null | undefined): string {
    if (perSec == null) return '—';
    if (perSec >= 1000) return `${(perSec / 1000).toFixed(1)}k/s`;
    return `${perSec}/s`;
  }
}
